import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertToeBoardExtraBody, ToeBoardExtra, UpdateToeBoardExtraBody } from "./types/toeBoardExtras";

export const useToeBoardExtrasApi = () => {
  const api = useApi();

  const getAdminToeBoardExtra = useCallback(
    (toeBoardExtraId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/toe_board_extras/${toeBoardExtraId}`;
      return useSWRMutation<ToeBoardExtra, CustomError, string>(url, (url) => api.get<ToeBoardExtra>({ url, signal }));
    },
    [api],
  );

  const useAdminToeBoardExtras = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/toe_board_extras`, query);
      return useSWRImmutable<Paginated<ToeBoardExtra>, CustomError, string>(url, (url) =>
        api.get<Paginated<ToeBoardExtra>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminToeBoardExtras = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/toe_board_extras`, query);
      return api.get<Paginated<ToeBoardExtra>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminToeBoardExtra = useCallback(
    async (body: InsertToeBoardExtraBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/toe_board_extras`;
      const id = await api.post<{ id: string }, InsertToeBoardExtraBody>(url, body);
      mutateCache([url, `/admin/settings/border/toe_boards`]);
      return id;
    },
    [api],
  );

  const updateAdminToeBoardExtra = useCallback(
    async (toeBoardExtraId: string, body: UpdateToeBoardExtraBody): Promise<void> => {
      const url = `/admin/settings/border/toe_board_extras/${toeBoardExtraId}`;
      await api.put<UpdateToeBoardExtraBody>(url, body);
      mutateCache(["/admin/settings/border/toe_board_extras", `/admin/settings/border/toe_boards`]);
    },
    [api],
  );

  const updateAdminToeBoardExtraVisibility = useCallback(
    async (toeBoardExtraId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/toe_board_extras/${toeBoardExtraId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/toe_board_extras", `/admin/settings/border/toe_boards`]));
    },
    [api],
  );

  const updateAdminToeBoardExtraOrder = useCallback(
    async (toeBoardExtraId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/toe_board_extras/${toeBoardExtraId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/toe_board_extras", `/admin/settings/border/toe_boards`]));
    },
    [api],
  );

  const deleteAdminToeBoardExtra = useCallback(
    async (toeBoardExtra: ToeBoardExtra): Promise<void> => {
      const url = `/admin/settings/border/toe_board_extras/${toeBoardExtra.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/toe_board_extras`, `/admin/settings/border/toe_boards`]);
    },
    [api],
  );

  return {
    getAdminToeBoardExtra,
    insertAdminToeBoardExtra,
    updateAdminToeBoardExtra,
    updateAdminToeBoardExtraVisibility,
    updateAdminToeBoardExtraOrder,
    deleteAdminToeBoardExtra,
    useAdminToeBoardExtras,
    getAdminToeBoardExtras,
  };
};
